/**
 * usePermission / useRolePermissions / useDepartments
 *
 * 仕様書: docs/01_spec/spec_user_management.md
 *
 * 現在のユーザーの部署×レベルに対応する rolePermissions ドキュメントを購読し、
 * 任意の featureId × action の可否を判定するフック群を提供する。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useMemo, useState } from 'react';

import {
  hasPermission as sharedHasPermission,
  makeRoleKey,
  type Action,
  type Department,
  type DepartmentId,
  type Level,
  type PermissionMap,
  type RolePermissions,
} from '@goenhub/shared';

import { useAuth } from '@/hooks/useAuth';
import { db, functions } from '@/lib/firebase';

/**
 * 現在のユーザーの permissions（dept × level に対応するマップ）を購読する。
 * - isOwner=true の場合、permissions は null だが hasPermission は常に true を返す。
 * - status='pending' の場合は permissions は null。
 */
export function useMyPermissions(): {
  isOwner: boolean;
  permissions: PermissionMap | null;
  loading: boolean;
} {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<PermissionMap | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    if (user.isOwner) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    const key = makeRoleKey(user.dept, user.level);
    const ref = doc(db, 'rolePermissions', key);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        setPermissions((data?.permissions ?? {}) as PermissionMap);
        setLoading(false);
      },
      (err) => {
        console.error('[useMyPermissions] snapshot error:', err);
        setPermissions({});
        setLoading(false);
      },
    );
    return () => unsub();
  }, [user]);

  return { isOwner: Boolean(user?.isOwner), permissions, loading };
}

/**
 * 指定 featureId × action の可否を返す。
 */
export function usePermission(featureId: string, action: Action = 'view'): boolean {
  const { isOwner, permissions } = useMyPermissions();
  return useMemo(
    () => sharedHasPermission({ isOwner, permissions, featureId, action }),
    [isOwner, permissions, featureId, action],
  );
}

/**
 * 単一機能の全 4 アクション可否をまとめて返す（ボタン群の出し分け用）。
 */
export function useFeatureActions(featureId: string): Record<Action, boolean> {
  const { isOwner, permissions } = useMyPermissions();
  return useMemo(
    () => ({
      view: sharedHasPermission({ isOwner, permissions, featureId, action: 'view' }),
      create: sharedHasPermission({ isOwner, permissions, featureId, action: 'create' }),
      edit: sharedHasPermission({ isOwner, permissions, featureId, action: 'edit' }),
      delete: sharedHasPermission({ isOwner, permissions, featureId, action: 'delete' }),
    }),
    [isOwner, permissions, featureId],
  );
}

// ─────────────────────────────────────────────
// departments / rolePermissions の管理（owner 用）
// ─────────────────────────────────────────────

export interface DepartmentRecord extends Department {
  id: DepartmentId;
}

export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: async (): Promise<DepartmentRecord[]> => {
      const q = query(collection(db, 'departments'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Department) }));
    },
  });
}

export interface RolePermissionsRecord extends RolePermissions {
  id: string;
}

export function useAllRolePermissions() {
  return useQuery({
    queryKey: ['rolePermissions'],
    queryFn: async (): Promise<RolePermissionsRecord[]> => {
      const snap = await getDocs(collection(db, 'rolePermissions'));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as RolePermissions) }));
    },
  });
}

interface UpsertDepartmentInput {
  id: string;
  name: string;
  order?: number;
}

export function useUpsertDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertDepartmentInput) => {
      const fn = httpsCallable(functions, 'upsertDepartment');
      const res = await fn(input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      qc.invalidateQueries({ queryKey: ['rolePermissions'] });
    },
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const fn = httpsCallable(functions, 'deleteDepartment');
      const res = await fn({ id });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      qc.invalidateQueries({ queryKey: ['rolePermissions'] });
    },
  });
}

interface UpdateRolePermissionsInput {
  dept: DepartmentId;
  level: Level;
  permissions: PermissionMap;
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateRolePermissionsInput) => {
      const fn = httpsCallable(functions, 'updateRolePermissions');
      const res = await fn(input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rolePermissions'] }),
  });
}

export function useInitDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable(functions, 'initUserManagementDefaults');
      const res = await fn({});
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      qc.invalidateQueries({ queryKey: ['rolePermissions'] });
    },
  });
}

export function useCleanupRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable(functions, 'cleanupRolePermissions');
      const res = await fn({});
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rolePermissions'] }),
  });
}
